import * as React from 'react';
import { art, asian, coins, collectibles, fashion, furniture, home, jewelry } from './megaNavLinks';
import { isUiMobile } from '@/redux/modules/browser';
import { Ref } from '@/types/Ref';
import { useAppSelector } from '@/redux/hooks';
import FindAuctions from './FindAuctions';
import LinkContainer from './LinkContainer';
import MegaNavLink from './MegaNavLink';
import noop from '@/utils/noop';
import styled from 'styled-components';

const Navigation = () => {
    const targetRef: Ref = React.useRef();
    const [active, setActive] = React.useState(0);
    const mobile = useAppSelector(isUiMobile);

    const isPhone = mobile && mobile !== 'tablet';

    const openNav = React.useCallback((key: number) => {
        return () => {
            setActive(key);
        };
    }, []);

    const closeNav = React.useCallback(() => {
        if (active) {
            setActive(0);
        }
    }, [active]);

    const tabletNavigation = React.useCallback(
        (link: string, key: number) => {
            return () => {
                if (active !== key) {
                    setActive(key);
                } else {
                    window.location.assign(link);
                    setActive(0);
                }
            };
        },
        [active]
    );

    const handleClickOutside = React.useCallback(
        (event: any) => {
            if (mobile === 'tablet') {
                if (targetRef && targetRef.current.contains && !targetRef.current.contains(event.target)) {
                    closeNav();
                }
            }
        },
        [closeNav, mobile]
    );

    React.useEffect(() => {
        window.addEventListener('touchstart', handleClickOutside, false);
        return () => {
            window.removeEventListener('touchstart', handleClickOutside, false);
        };
    }, [handleClickOutside]);

    return (
        <StyledNavigationContainer ref={(ref) => (targetRef.current = ref)}>
            <StyledNavigation onMouseLeave={!mobile ? closeNav : noop}>
                <StyledScrollContainer>
                    <FindAuctions />
                    <StyledScrollItem>|</StyledScrollItem>
                    <StyledNavBar>
                        <MegaNavLink
                            active={active === 1}
                            closeNav={closeNav}
                            link="/c/art/1/"
                            mobile={mobile}
                            msgId="navigation.art"
                            openNav={openNav(1)}
                            tabletNavigation={tabletNavigation('/c/art/1/', 1)}
                        />
                        <MegaNavLink
                            active={active === 2}
                            closeNav={closeNav}
                            link="/c/jewelry/6/"
                            mobile={mobile}
                            msgId="navigation.jewelry"
                            openNav={openNav(2)}
                            tabletNavigation={tabletNavigation('/c/jewelry/6/', 2)}
                        />
                        <MegaNavLink
                            active={active === 3}
                            closeNav={closeNav}
                            link="/c/asian/158/"
                            mobile={mobile}
                            msgId="navigation.asian_antiques"
                            openNav={openNav(3)}
                            tabletNavigation={tabletNavigation('/c/asian/158/', 3)}
                        />
                        <MegaNavLink
                            active={active === 4}
                            closeNav={closeNav}
                            link="/c/furniture/4/"
                            mobile={mobile}
                            msgId="navigation.furniture"
                            openNav={openNav(4)}
                            tabletNavigation={tabletNavigation('/c/furniture/4/', 4)}
                        />
                        <MegaNavLink
                            active={active === 5}
                            closeNav={closeNav}
                            link="/c/collectibles/2/"
                            mobile={mobile}
                            msgId="navigation.collectibles"
                            openNav={openNav(5)}
                            tabletNavigation={tabletNavigation('/c/collectibles/2/', 5)}
                        />
                        <MegaNavLink
                            active={active === 6}
                            closeNav={closeNav}
                            link="/c/coins-currency-stamps/24/"
                            mobile={mobile}
                            msgId="navigation.coins"
                            openNav={openNav(6)}
                            tabletNavigation={tabletNavigation('/c/coins-currency-stamps/24/', 6)}
                        />
                        <MegaNavLink
                            active={active === 7}
                            closeNav={closeNav}
                            link="/c/home-garden/5/"
                            mobile={mobile}
                            msgId="navigation.home_and_garden"
                            openNav={openNav(7)}
                            tabletNavigation={tabletNavigation('/c/home-garden/5/', 7)}
                        />
                        <MegaNavLink
                            active={active === 8}
                            closeNav={closeNav}
                            link="/c/fashion/3/"
                            mobile={mobile}
                            msgId="navigation.fashion"
                            openNav={openNav(8)}
                            tabletNavigation={tabletNavigation('/c/fashion/3/', 8)}
                        />
                        <MegaNavLink
                            active={active === -2}
                            closeNav={closeNav}
                            link="/c/"
                            mobile={mobile}
                            msgId="navigation.explore_categories"
                            openNav={openNav(-2)}
                            tabletNavigation={tabletNavigation('/c/', 0)}
                        />
                    </StyledNavBar>
                </StyledScrollContainer>
                {!isPhone && (
                    <div>
                        <LinkContainer
                            active={active === 1}
                            closeNav={closeNav}
                            id="navigation.1"
                            linkArray={art}
                            mobile={mobile}
                        />
                        <LinkContainer
                            active={active === 2}
                            closeNav={closeNav}
                            id="navigation.6"
                            linkArray={jewelry}
                            mobile={mobile}
                        />
                        <LinkContainer
                            active={active === 3}
                            closeNav={closeNav}
                            id="navigation.158"
                            linkArray={asian}
                            mobile={mobile}
                        />
                        <LinkContainer
                            active={active === 4}
                            closeNav={closeNav}
                            id="navigation.4"
                            linkArray={furniture}
                            mobile={mobile}
                        />
                        <LinkContainer
                            active={active === 5}
                            closeNav={closeNav}
                            id="navigation.2"
                            linkArray={collectibles}
                            mobile={mobile}
                        />
                        <LinkContainer
                            active={active === 6}
                            closeNav={closeNav}
                            id="navigation.24"
                            linkArray={coins}
                            mobile={mobile}
                        />
                        <LinkContainer
                            active={active === 7}
                            closeNav={closeNav}
                            id="navigation.5"
                            linkArray={home}
                            mobile={mobile}
                        />
                        <LinkContainer
                            active={active === 8}
                            closeNav={closeNav}
                            id="navigation.3"
                            linkArray={fashion}
                            mobile={mobile}
                        />
                    </div>
                )}
            </StyledNavigation>
        </StyledNavigationContainer>
    );
};

export default React.memo(Navigation);

const StyledNavigationContainer = styled.div`
    width: 100%;
    position: relative;
    z-index: 12;
`;

const StyledNavigation = styled.div`
    z-index: 110;
    position: relative;
    white-space: nowrap;
    line-height: 1;
    height: 48px;
    display: flex;
    align-items: center;

    @media (min-width: ${({ theme }) => theme.breakpoints.pageWidth}) {
        margin: 0 auto;
        max-width: ${({ theme }) => theme.breakpoints.pageWidth};
    }

    @media (min-width: 976px) {
        height: 55px;
    }
`;

const StyledScrollContainer = styled.div`
    color: ${({ theme }) => theme.colors.grey400};
    display: flex;
    align-items: center;
    -ms-overflow-style: none; /* IE 10+ */
    overflow: -moz-scrollbars-none; /* Firefox */
    height: 100%;
    padding: 0 1px 0 20px;
    overflow-y: hidden;

    &::-webkit-scrollbar {
        display: none;
    }
`;

const StyledScrollItem = styled.div`
    display: inline-block;
    padding: 10px 13px;
    font-size: 14px;
    font-family: ${({ theme }) => theme.typography.fontSecondary};
    color: ${({ theme }) => theme.colors.grey400};
    text-decoration: none;
    padding-left: 17px;

    &:first-child {
        padding-left: 0;
    }

    @media (min-width: 976px) {
        font-size: 16px;
        padding: 10px 4px;
    }
`;

const StyledNavBar = styled.div`
    display: flex;
    align-items: center;
    overflow: visible;
    height: 100%;
    padding-right: 5px;
`;
